<template>
  <b-card no-body>
    <b-card-header>
      <h2>Inputs</h2>
    </b-card-header>
    <b-card-body>

      <h4>Project Information</h4>
      <b-table :items="section1Items" fixed small />
      <hr>

      <h4>Existing Section: Parameters</h4>
      <b-table :items="section2aItems" fixed small />
      <hr>

      <h4>Existing Section: History</h4>
      <b-table :items="section2bItems" fixed small />
      <hr>


      <h4>Existing Section: Surface Structure</h4>
      <b-table :items="section2cItems" fixed small />
      <div class="pagebreak"></div>
      <hr>

      <h4>Existing Section: Subsurface Structure</h4>
      <b-table :items="section2dItems" fixed small />

      <hr>
      <h4>Distress Survey</h4>
      <b-table :items="section3Items" fixed small />
      <hr>

      <h4>Additional Factors</h4>
      <b-table :items="section4Items" fixed small />

    </b-card-body>
  </b-card>
</template>

<script>
  import { mapFields } from 'vuex-map-fields'

  // Helper functions to convert stored values to human-readable display values
  const trueFalseDisplay = (v) => {
    if (v === 1) {
      return "True"
    } else if (v===0) {
      return "False"
    } else if (v === -1) {
      return "Unsure"
    } else {
      return "Entry not found"
    }
  };

  const pavementTypeDisplay = (code) => {
    const types = [
      {text: "Flexible (HMA or other bituminous layers)", value: 1},
      {text: "Rigid (JPCP, JRCP, or CRCP)", value: 2},
      {text: "Composite (HMA over rigid layer)", value: 3}
    ];

    return types.filter((v) => v.value === code)[0].text
  };

  const lastRenewalTypeDisplay = (code) => {
    const treatmentOptions = [
      {text: "Unsure / I don't know", value: 0},
      {text: "Overlay (or FDR with Overlay)", value: 1},
      {text: "Chip / Cape Seal (or FDR with Chip/Cape Seal)", value: 2},
      {text: "New Construction or Full Reconstruction", value: 3}
    ];
    return treatmentOptions.filter((v) => v.value === code)[0].text
  };

  const distressDisplay = (v) => {
    let displayValue = "";
    switch (v) {
      case 0:
        displayValue = "None";
        break;
      case 1:
        displayValue = "Low";
        break;
      case 2:
        displayValue = "Medium";
        break;
      case 3:
        displayValue = "High";
        break;
    }
    return displayValue
  };



  export default {
    name: "UserInputsReport",
    computed: {
      ...mapFields('rx', [
        // Section 1
        "currentProject.projectName",
        "currentProject.projectId",
        "currentProject.city",
        "currentProject.state",
        "currentProject.owner",
        "currentProject.locationDescription",
        "currentProject.projectDescription",

        //2a
        "currentProject.pavementType",
        "currentProject.segmentLengthFt",
        "currentProject.segmentWidthFt",
        "currentProject.intersectionPresent",
        "currentProject.twoWayTurnPresent",

        //2b
        "currentProject.lastPCRYear",
        "currentProject.lastPCRReading",
        "currentProject.lastTreatmentYear",
        "currentProject.lastKnownTreatment",

        //2c
        "currentProject.hasParkingLanes",
        "currentProject.hasParkingLanesDescription",
        "currentProject.hasShoulder",
        "currentProject.hasShoulderDescription",
        "currentProject.hasBikeLane",
        "currentProject.hasBikeLaneDescription",
        "currentProject.hasCurb",
        "currentProject.hasCurbDescription",
        "currentProject.hasOtherSurface",
        "currentProject.hasOtherSurfaceDescription",

        //2d
        "currentProject.hasConcreteLayers",
        "currentProject.hasConcreteLayersDescription",
        "currentProject.hasTreatedBase",
        "currentProject.hasTreatedBaseDescription",
        "currentProject.hasShallowUtilities",
        "currentProject.hasShallowUtilitiesDescription",
        "currentProject.hasGeotextiles",
        "currentProject.hasGeotextilesDescription",
        "currentProject.hasOtherSubsurface",
        "currentProject.hasOtherSubsurfaceDescription",

        // Section 3
        "currentProject.alligatorCrackingSeverity",
        "currentProject.alligatorCrackingExtent",
        "currentProject.blockCrackingSeverity",
        "currentProject.blockCrackingExtent",
        "currentProject.longitudinalCrackingSeverity",
        "currentProject.transverseCrackingSeverity",
        "currentProject.ruttingSeverity",
        "currentProject.bleedFlushSeverity",
        "currentProject.ravelSeverity",
        "currentProject.patchingSeverity",
        "currentProject.distressNotes",

        // Section 4
        "currentProject.isWheelPathFlushing",
        "currentProject.requireSmoothMacrotexture",
        "currentProject.requireMaintainCurbReveal",
        "currentProject.existingSubgradeCbrUnderThreshold"

      ]),
      section1Items() {
        return [
          { field: "Project Name", value: this.projectName },
          { field: "Project ID", value: this.projectId },
          { field: "City", value: this.city },
          { field: "State", value: this.state },
          { field: "Owner", value: this.owner },
          { field: "Location Description", value: this.locationDescription },
          { field: "Project Description", value: this.projectDescription }
        ]
      },

      section2aItems() {
        return [
            { field: "Pavement Type", value: pavementTypeDisplay(this.pavementType) },
            { field: "Segment Length (Ft)", value: this.segmentLengthFt },
            { field: "Segment Width (Ft)", value: this.segmentWidthFt },
            { field: "Is Intersection Present", value: trueFalseDisplay(this.intersectionPresent) },
            { field: "Is Two-Way Turn Present", value: trueFalseDisplay(this.twoWayTurnPresent) },
          ]
      },

      section2bItems() {
        return [
            { field: "Last PCR Year", value: this.lastPCRYear },
            { field: "Last PCR Reading", value: this.lastPCRReading },
            { field: "Last Treatment Year", value: this.lastTreatmentYear },
            { field: "Last Known Treatment", value: lastRenewalTypeDisplay(this.lastKnownTreatment) },
          ]
      },

      section2cItems() {
        return [
            { field: "Has Parking Lanes", value: trueFalseDisplay(this.hasParkingLanes) },
            { field: "Parking Lanes Description", value: this.hasParkingLanesDescription || '-' },
            { field: "Has Shoulder", value: trueFalseDisplay(this.hasShoulder) },
            { field: "Shoulder Description", value: this.hasShoulderDescription || '-' },
            { field: "Has Bike Lane", value: trueFalseDisplay(this.hasBikeLane) },
            { field: "Bike Lane Description", value: this.hasBikeLaneDescription || '-' },
            { field: "Has Curb", value: trueFalseDisplay(this.hasCurb) },
            { field: "Curb Description", value: this.hasCurbDescription || '-' },
            { field: "Has Other Surface Features", value: trueFalseDisplay(this.hasOtherSurface) },
            { field: "Other Surface Features Description", value: this.hasOtherSurfaceDescription || '-' },
          ]
      },

      section2dItems() {
        return [
            { field: "Has Concrete Layers", value: trueFalseDisplay(this.hasConcreteLayers) },
            { field: "Concrete Layer Description", value: this.hasConcreteLayersDescription || "-" },
            { field: "Has Treated Base", value: trueFalseDisplay(this.hasTreatedBase) },
            { field: "Treated Base Description", value: this.hasTreatedBaseDescription || "-" },
            { field: "Has Shallow Utilities", value: trueFalseDisplay(this.hasShallowUtilities) },
            { field: "Shallow Utilities Description", value: this.hasShallowUtilitiesDescription || "-" },
            { field: "Has Geotextiles", value: trueFalseDisplay(this.hasGeotextiles) },
            { field: "Geotextile Description", value: this.hasGeotextilesDescription || "-" },
            { field: "Has Other Subsurface Features", value: trueFalseDisplay(this.hasOtherSubsurface) },
            { field: "Other Subsurface Feature Description", value: this.hasOtherSubsurfaceDescription || "-" },
          ]
      },

      section3Items() {
        return [
            { field: "Alligator Cracking Severity", value: distressDisplay(this.alligatorCrackingSeverity) },
            { field: "Alligator Cracking Extent", value: this.alligatorCrackingExtent.toString() + "%" },
            { field: "Block Cracking Severity", value: distressDisplay(this.blockCrackingSeverity) },
            // { field: "", value: distressDisplay(this.blockCrackingExtent) },
            { field: "Longitudinal Cracking Severity", value: distressDisplay(this.longitudinalCrackingSeverity) },
            { field: "Transverse Cracking Severity", value: distressDisplay(this.transverseCrackingSeverity) },
            { field: "Rutting Severity", value: distressDisplay(this.ruttingSeverity) },
            { field: "Bleed/Flush Severity", value: distressDisplay(this.bleedFlushSeverity) },
            { field: "Ravel Severity", value: distressDisplay(this.ravelSeverity) },
            { field: "Patching Severity", value: distressDisplay(this.patchingSeverity) },
            { field: "Distress Notes", value: distressDisplay(this.distressNotes) },
          ]
      },

      section4Items() {
        return [
            { field: "Is wheel path flushing?", value: trueFalseDisplay(this.isWheelPathFlushing) },
            { field: "Require smooth macrotexture?", value: trueFalseDisplay(this.requireSmoothMacrotexture) },
            { field: "Require curb reveal?", value: trueFalseDisplay(this.requireMaintainCurbReveal) },
            { field: "Is existing subgrade CBR <8?", value: trueFalseDisplay(this.existingSubgradeCbrUnderThreshold) },
          ]
      }
    },
  }

</script>

<style scoped>

</style>